import React, { useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminSubmissions = () => {
  const [password, setPassword] = useState('');
  const [submissions, setSubmissions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [authenticated, setAuthenticated] = useState(false);

  const fetchSubmissions = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/admin/submissions`, {
        headers: { 'X-Admin-Password': password },
      });
      setSubmissions(res.data.submissions || []);
      setAuthenticated(true);
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Wrong password. Try again.');
      } else {
        setError('Something went wrong fetching submissions.');
      }
      setSubmissions(null);
      setAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    } catch { return iso; }
  };

  // CSV export — client-side, no server round-trip
  const downloadCsv = () => {
    if (!submissions || submissions.length === 0) return;
    const headers = ['Date', 'Name', 'Email', 'Phone', 'Website', 'Challenge', 'Email Sent', 'Submitted At (ISO)', 'Submission ID'];
    const escape = (v) => {
      const s = v == null ? '' : String(v);
      // Wrap in quotes and escape existing quotes per RFC 4180
      return `"${s.replace(/"/g, '""')}"`;
    };
    const rows = submissions.map((s) => [
      formatDate(s.submitted_at),
      s.name || '',
      s.email || '',
      s.phone || '',
      s.website || '',
      s.challenge || '',
      s.email_sent ? 'Sent' : 'Failed',
      s.submitted_at || '',
      s.id || '',
    ].map(escape).join(','));
    const csv = [headers.map(escape).join(','), ...rows].join('\r\n');
    // Add UTF-8 BOM so Excel opens with correct encoding for non-ASCII challenge text
    const blob = new Blob(['\ufeff', csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const now = new Date();
    const stamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}-${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`;
    const link = document.createElement('a');
    link.href = url;
    link.download = `sanjib-roy-submissions-${stamp}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const styles = {
    page: { minHeight: '100vh', background: '#0b1220', color: '#e2e8f0', padding: '48px 24px', fontFamily: 'system-ui, -apple-system, sans-serif' },
    container: { maxWidth: 1200, margin: '0 auto' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32, flexWrap: 'wrap', gap: 12 },
    title: { fontSize: 24, fontWeight: 700, margin: 0 },
    countBadge: { background: 'rgba(14,165,233,0.15)', color: '#38bdf8', padding: '4px 12px', borderRadius: 999, fontSize: 13, fontWeight: 600 },
    card: { background: '#111e33', border: '1px solid rgba(148,163,184,0.15)', borderRadius: 12, padding: 32 },
    loginCard: { maxWidth: 420, margin: '80px auto', padding: 40 },
    input: { width: '100%', padding: '12px 14px', fontSize: 15, borderRadius: 8, border: '1px solid rgba(148,163,184,0.2)', background: '#0f172a', color: '#f1f5f9', boxSizing: 'border-box' },
    btn: { width: '100%', padding: '12px 14px', fontSize: 15, fontWeight: 600, borderRadius: 8, border: 0, background: '#0ea5e9', color: '#04121f', cursor: 'pointer', marginTop: 16 },
    btnSecondary: { padding: '10px 18px', fontSize: 13, fontWeight: 600, borderRadius: 8, border: '1px solid rgba(148,163,184,0.3)', background: 'transparent', color: '#e2e8f0', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 },
    err: { color: '#fca5a5', marginTop: 12, fontSize: 14 },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 14 },
    th: { textAlign: 'left', padding: '12px 14px', background: '#0f172a', color: '#94a3b8', fontWeight: 600, fontSize: 12, letterSpacing: 1, textTransform: 'uppercase', borderBottom: '1px solid rgba(148,163,184,0.15)' },
    td: { padding: '14px', borderBottom: '1px solid rgba(148,163,184,0.08)', verticalAlign: 'top' },
    email: { color: '#38bdf8', textDecoration: 'none' },
    challenge: { color: '#cbd5e1', maxWidth: 380, whiteSpace: 'pre-wrap' },
    pill: { display: 'inline-block', padding: '2px 10px', borderRadius: 999, fontSize: 11, fontWeight: 600 },
    pillOk: { background: 'rgba(34,197,94,0.15)', color: '#4ade80' },
    pillFail: { background: 'rgba(239,68,68,0.15)', color: '#fca5a5' },
    empty: { textAlign: 'center', padding: 48, color: '#94a3b8' },
  };

  if (!authenticated) {
    return (
      <div style={styles.page} data-testid="admin-login-page">
        <div style={{ ...styles.card, ...styles.loginCard }}>
          <h1 style={styles.title}>Admin · Submissions</h1>
          <p style={{ color: '#94a3b8', margin: '10px 0 24px', fontSize: 14 }}>
            Enter the admin password to view contact submissions.
          </p>
          <form onSubmit={fetchSubmissions}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Admin password"
              style={styles.input}
              autoFocus
              data-testid="admin-password-input"
            />
            <button type="submit" style={styles.btn} disabled={loading} data-testid="admin-login-btn">
              {loading ? 'Verifying…' : 'View Submissions'}
            </button>
            {error && <div style={styles.err} data-testid="admin-login-error">{error}</div>}
          </form>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page} data-testid="admin-submissions-page">
      <div style={styles.container}>
        <div style={styles.header}>
          <h1 style={styles.title}>Contact Submissions</h1>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <span style={styles.countBadge} data-testid="admin-submissions-count">
              {submissions.length} total
            </span>
            <button
              type="button"
              onClick={downloadCsv}
              disabled={submissions.length === 0}
              style={{ ...styles.btnSecondary, opacity: submissions.length === 0 ? 0.4 : 1, cursor: submissions.length === 0 ? 'not-allowed' : 'pointer' }}
              data-testid="admin-export-csv-btn"
            >
              ↓ Export CSV
            </button>
          </div>
        </div>
        <div style={styles.card}>
          {submissions.length === 0 ? (
            <div style={styles.empty}>No submissions yet.</div>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Date</th>
                  <th style={styles.th}>Name</th>
                  <th style={styles.th}>Email</th>
                  <th style={styles.th}>Phone</th>
                  <th style={styles.th}>Website</th>
                  <th style={styles.th}>Challenge</th>
                  <th style={styles.th}>Email Sent</th>
                </tr>
              </thead>
              <tbody data-testid="admin-submissions-table">
                {submissions.map((s, i) => (
                  <tr key={s.id || i}>
                    <td style={styles.td}>{formatDate(s.submitted_at)}</td>
                    <td style={styles.td}><strong>{s.name}</strong></td>
                    <td style={styles.td}>
                      <a href={`mailto:${s.email}`} style={styles.email}>{s.email}</a>
                    </td>
                    <td style={styles.td}>{s.phone || '—'}</td>
                    <td style={styles.td}>
                      {s.website ? (
                        <a href={s.website} target="_blank" rel="noopener noreferrer" style={styles.email}>
                          {s.website}
                        </a>
                      ) : '—'}
                    </td>
                    <td style={{ ...styles.td, ...styles.challenge }}>{s.challenge}</td>
                    <td style={styles.td}>
                      <span style={{ ...styles.pill, ...(s.email_sent ? styles.pillOk : styles.pillFail) }}>
                        {s.email_sent ? '✓ Sent' : '✕ Failed'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminSubmissions;
